import { useEffect, useState } from "react";

import { useParams } from "@tanstack/react-router";

import { apiClient } from "@/api/apiClient";
import { getAllFilesApiV1FilesGet } from "@/api/generated/sdk.gen";
import { Button } from "@/components/ui/button";

type FilteredFile = {
  name: string;
  author: string | null;
  classification: string;
  date: string | null;
  short_summary: string | null;
  full_summary: string | null;
};

const ResultJsonPage = () => {
  const { id: caseNumber } = useParams({ strict: false }) as { id: string };
  const [jsonData, setJsonData] = useState<
    FilteredFile[] | { error: string } | null
  >(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchAndDisplayJson = async () => {
      if (!caseNumber) {
        setJsonData({ error: "Case number is required" });
        return;
      }

      try {
        const response = await getAllFilesApiV1FilesGet({
          client: apiClient,
          query: {
            case_number: caseNumber,
          },
        });

        if ("error" in response && response.error) {
          console.error("Failed to fetch files:", response.error);
          setJsonData({ error: "Failed to fetch files" });
          return;
        }

        // Filter to only include the specified fields
        const filteredFiles: FilteredFile[] = (response.data || []).map((file) => ({
          name: file.name,
          author: file.author ?? null,
          classification: file.classification,
          date: file.date ?? null,
          short_summary: file.short_summary ?? null,
          full_summary: file.full_summary ?? null,
        }));

        setJsonData(filteredFiles);
      } catch (error) {
        console.error("Error fetching files:", error);
        setJsonData({ error: "Failed to fetch files" });
      }
    };

    fetchAndDisplayJson();
  }, [caseNumber]);

  const handleCopy = async () => {
    if (jsonData === null) return;

    const jsonString = JSON.stringify(jsonData, null, 2);
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (jsonData === null) {
    return (
      <div style={{ padding: "20px" }}>
        <pre>Loading...</pre>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          marginBottom: "12px",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={handleCopy} variant="outline" size="sm">
          {copied ? "Copied!" : "Copy JSON"}
        </Button>
      </div>
      <pre
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          padding: "16px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          border: "1px solid #e0e0e0",
          fontSize: "14px",
          lineHeight: "1.5",
          overflow: "auto",
          maxHeight: "calc(100vh - 120px)",
          userSelect: "text",
        }}
      >
        {JSON.stringify(jsonData, null, 2)}
      </pre>
    </div>
  );
};

export default ResultJsonPage;
